import { FileText, FolderOpen, MoreHorizontal, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiError, deleteDocument, listDocuments } from "@/api/client";
import type { DocumentListItem } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { STANDINGS, standing } from "@/flow/standing";
import type { Standing } from "@/flow/standing";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Dialog";
import { EmptyState } from "@/ui/EmptyState";
import { formatBytes } from "@/ui/format";
import { Input, Select } from "@/ui/Input";
import { Menu, MenuItem } from "@/ui/Menu";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Skeleton } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

/** As many as the API returns at once; a launch customer has a few dozen sets. */
const PAGE = 200;
const SEARCH_DELAY_MS = 250;

type Sort = "newest" | "oldest" | "name";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function sorted(rows: DocumentListItem[], sort: Sort): DocumentListItem[] {
  const copy = [...rows];
  if (sort === "name") {
    copy.sort((a, b) => a.filename.localeCompare(b.filename, undefined, { numeric: true }));
  } else if (sort === "oldest") {
    copy.reverse();
  }
  return copy;
}

/**
 * The organization's drawing sets: where each stands, and the way back into it. Search
 * asks the server; the status filter and the sort work on what is loaded.
 */
export function SetsScreen() {
  const navigate = useNavigate();
  const canUpload = useCan("documents:upload");
  const canDelete = useCan("documents:delete");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<DocumentListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Standing["key"] | "all">("all");
  const [sort, setSort] = useState<Sort>("newest");
  const [removing, setRemoving] = useState<DocumentListItem | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (q: string) => {
    try {
      const page = await listDocuments({ q, limit: PAGE });
      setRows(page.documents);
      setTotal(page.total);
      setError(null);
    } catch (err) {
      setRows((now) => now ?? []);
      setError(err instanceof ApiError ? err.detail : "Could not reach the server.");
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(query), query ? SEARCH_DELAY_MS : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);

  const shown = useMemo(() => {
    const kept = (rows ?? []).filter(
      (row) => status === "all" || standing(row, row.latest_run).key === status,
    );
    return sorted(kept, sort);
  }, [rows, status, sort]);

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await deleteDocument(removing.id);
      setRemoving(null);
      await load(query);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not remove the set.");
      setRemoving(null);
    } finally {
      setBusy(false);
    }
  };

  const newSet = canUpload ? (
    <Button asChild variant="primary">
      <Link to="/sets/new">
        <Plus aria-hidden="true" />
        New set
      </Link>
    </Button>
  ) : null;

  const searching = query.trim() !== "" || status !== "all";

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
      <PageHeader
        title="Sets"
        description="Drawing sets your organization has uploaded, and where each take-off stands."
        actions={newSet}
      />

      <div className="mt-6 flex flex-wrap items-center gap-2" role="search">
        <label className="min-w-56 flex-1">
          <span className="sr-only">Search sets by file name</span>
          <Input
            type="search"
            icon={<Search />}
            placeholder="Search by file name"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <label>
          <span className="sr-only">Status</span>
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value as Standing["key"] | "all")}
          >
            <option value="all">Any status</option>
            {STANDINGS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span className="sr-only">Sort</span>
          <Select value={sort} onChange={(event) => setSort(event.target.value as Sort)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name</option>
          </Select>
        </label>
      </div>

      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}

      <div className="mt-4">
        {rows === null ? (
          <div className="space-y-2" role="status" aria-label="Loading sets">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : shown.length === 0 ? (
          searching ? (
            <EmptyState icon={<Search />} title="No sets match">
              Try another name, or clear the status filter.
            </EmptyState>
          ) : (
            <EmptyState icon={<FolderOpen />} title="No sets yet" action={newSet}>
              Upload a drawing set as one PDF to start its take-off.
            </EmptyState>
          )
        ) : (
          <>
            <Table aria-label="Sets">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th className="hidden text-right sm:table-cell">Sheets</Th>
                  <Th className="hidden text-right md:table-cell">Size</Th>
                  <Th>Status</Th>
                  <Th className="hidden lg:table-cell">Uploaded</Th>
                  <Th className="w-10">
                    <span className="sr-only">Actions</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => {
                  const at = standing(row, row.latest_run);
                  return (
                    <tr key={row.id} className="hover:bg-sunken/50">
                      <Td className="max-w-0 w-full">
                        <Link
                          to={`/documents/${row.id}`}
                          className="flex min-w-0 items-center gap-2.5 font-medium text-ink no-underline hover:text-accent"
                          title={row.filename}
                        >
                          <FileText aria-hidden="true" className="size-4 shrink-0 text-muted" />
                          <span className="truncate">{row.filename}</span>
                        </Link>
                      </Td>
                      <Td className="tabular hidden text-right text-ink-2 sm:table-cell">
                        {row.page_count ?? "…"}
                      </Td>
                      <Td className="tabular hidden text-right whitespace-nowrap text-ink-2 md:table-cell">
                        {formatBytes(row.size_bytes)}
                      </Td>
                      <Td>
                        <Badge tone={at.tone} dot>
                          {at.label}
                        </Badge>
                      </Td>
                      <Td className="hidden whitespace-nowrap text-ink-2 lg:table-cell">
                        {formatDate(row.created_at)}
                      </Td>
                      <Td className="text-right">
                        <Menu
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-7 px-0"
                              aria-label={`Actions for ${row.filename}`}
                            >
                              <MoreHorizontal aria-hidden="true" />
                            </Button>
                          }
                        >
                          <MenuItem onSelect={() => void navigate(`/documents/${row.id}`)}>
                            <FolderOpen aria-hidden="true" />
                            Open
                          </MenuItem>
                          {canDelete ? (
                            <MenuItem tone="danger" onSelect={() => setRemoving(row)}>
                              <Trash2 aria-hidden="true" />
                              Remove…
                            </MenuItem>
                          ) : null}
                        </Menu>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <p className="mt-3 text-xs text-muted">
              {shown.length === total
                ? `${total} ${total === 1 ? "set" : "sets"}`
                : `${shown.length} shown of ${total}`}
            </p>
          </>
        )}
      </div>

      <Dialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        label="Remove this set?"
      >
        <div className="space-y-4 p-4 text-sm text-ink-2">
          <p>
            <strong className="text-ink">{removing?.filename}</strong> and its take-off, decisions
            and markup will be removed. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setRemoving(null)}>Keep it</Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Remove set
            </Button>
          </div>
        </div>
      </Dialog>
    </main>
  );
}
